/**
 * A grade de fotos do posto: a foto de abertura (1 vaga) separada, acima, e o registro fotográfico em 4×2.
 * Uma foto é enviada por vez; cada vaga mostra seu estado (vazia, enviando, enviada, erro). O que o servidor
 * recusa volta com a mensagem dele e as legendas digitadas ficam, porque a lista só muda quando o envio dá certo.
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicPhoto, ErgonomicPhotoKind } from '../../../../../types/ergonomicWorkstation';
import { ConfirmDialog } from '../dialogs/ItemDialog';
import { isApiError } from '../../../../../types/common';
import { errorMessage } from '../wizardValidation';
import { EmptyPhotoSlot, type UploadAttempt } from './EmptyPhotoSlot';
import { PhotoSlot } from './PhotoSlot';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
const MAX_PHOTO_BYTES = 9 * 1024 * 1024;

const tooBigMessage = (fileName: string): string => `A foto "${fileName}" não foi enviada: passa do limite de 9 MB.`;

interface PhotoGridProps {
  workstationId: number;
  initialPhotos: ErgonomicPhoto[];
  limits: Record<ErgonomicPhotoKind, number>;
  canEdit: boolean;
  disabled: boolean;
  onSaved: (message: string) => void;
}

interface Attempt {
  kind: ErgonomicPhotoKind;
  state: UploadAttempt;
}

const gridSx = {
  m: 0,
  p: 0,
  display: 'grid',
  gap: 3,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
};

export const PhotoGrid: React.FC<PhotoGridProps> = ({ workstationId, initialPhotos, limits, canEdit, disabled, onSaved }) => {
  const [photos, setPhotos] = useState<ErgonomicPhoto[]>(initialPhotos);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [removing, setRemoving] = useState<ErgonomicPhoto | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const sending = attempt?.state.status === 'sending';

  const replacePhoto = (updated: ErgonomicPhoto) =>
    setPhotos((current) => current.map((photo) => (photo.id === updated.id ? updated : photo)));

  const upload = async (kind: ErgonomicPhotoKind, file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setAttempt({
        kind,
        state: { status: 'error', message: `A foto ${file.name} não foi enviada: o formato não é aceito. Use PNG ou JPEG.` },
      });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setAttempt({ kind, state: { status: 'error', message: tooBigMessage(file.name) } });
      return;
    }
    setAttempt({ kind, state: { status: 'sending', fileName: file.name } });
    try {
      const created = await ergonomicWorkstationService.uploadPhoto(workstationId, kind, file);
      setPhotos((current) => [...current, created]);
      setAttempt(null);
      onSaved('Foto enviada.');
    } catch (err: unknown) {
      setAttempt({
        kind,
        state: {
          status: 'error',
          // O proxy corta o corpo antes do servidor e responde 413: vira a frase do limite.
          message:
            isApiError(err) && err.status === 413
              ? tooBigMessage(file.name)
              : errorMessage(err, `A foto ${file.name} não foi enviada. Tente de novo em alguns minutos.`),
        },
      });
    }
  };

  const confirmRemoval = async () => {
    if (!removing) return;
    const target = removing;
    setRemoving(null);
    setRemoveError(null);
    try {
      await ergonomicWorkstationService.deletePhoto(workstationId, target.id);
      setPhotos((current) => current.filter((photo) => photo.id !== target.id));
      onSaved('Foto removida.');
    } catch (err: unknown) {
      setRemoveError(errorMessage(err, 'Não foi possível remover a foto.'));
    }
  };

  const section = (kind: ErgonomicPhotoKind, title: string, intro: string, fullReason: string, emptyLabel: string) => {
    const ofKind = photos.filter((photo) => photo.kind === kind);
    const limit = limits[kind];
    const free = Math.max(limit - ofKind.length, 0);
    const full = free === 0;
    const titleId = `photo-section-${kind.toLowerCase()}`;
    return (
      <Box component="section" aria-labelledby={titleId} sx={{ mb: 5 }}>
        <Typography id={titleId} variant="h6" component="h3" sx={{ mb: 1 }}>
          {title}
          <Typography component="span" variant="body1" sx={{ ml: 1 }}>
            {`${ofKind.length} de ${limit}`}
          </Typography>
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {intro}
        </Typography>

        {ofKind.length === 0 && !canEdit && (
          <Typography variant="body1">
            Ainda não há foto aqui. Quem tem acesso para editar o posto pode enviar.
          </Typography>
        )}

        <Box component="ul" sx={gridSx}>
          {ofKind.map((photo, index) => (
            <PhotoSlot
              key={photo.id}
              photo={photo}
              number={index + 1}
              canEdit={canEdit}
              disabled={disabled}
              onChanged={replacePhoto}
              onRemove={setRemoving}
              onSaved={onSaved}
            />
          ))}
          {canEdit &&
            Array.from({ length: free }, (_, index) => (
              <EmptyPhotoSlot
                key={`empty-${kind}-${index}`}
                label={emptyLabel}
                attempt={index === 0 && attempt?.kind === kind ? attempt.state : null}
                busy={sending}
                disabled={disabled}
                onFile={(file) => upload(kind, file)}
              />
            ))}
        </Box>

        {canEdit && full && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button variant="outlined" disabled sx={{ minHeight: 44 }}>
              {kind === 'ABERTURA' ? 'Adicionar foto de abertura' : 'Adicionar foto de registro'}
            </Button>
            <Typography variant="body1">{fullReason}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {removeError && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setRemoveError(null)}>
          {removeError}
        </Alert>
      )}
      {section(
        'ABERTURA',
        'Foto de abertura do posto',
        'Aparece ao lado do nome do posto, na primeira página do documento.',
        'A foto de abertura já foi enviada. Remova-a para enviar outra.',
        'Adicionar foto de abertura'
      )}
      {section(
        'REGISTRO',
        'Registro fotográfico',
        'PNG ou JPEG, até 9 MB. Marque "Adotada" quando a foto mostra uma medida já adotada.',
        `O limite de ${limits.REGISTRO} fotos de registro foi atingido. Remova uma foto para enviar outra.`,
        'Adicionar foto de registro'
      )}
      {removing && (
        <ConfirmDialog
          title="Remover esta foto?"
          message="A foto e a legenda dela somem do cadastro do posto e do documento."
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};
